/**
 * Alisha Floater Component
 *
 * Floating chat button and expandable chat window for communicating with Alisha.
 * Conversation persists across pages via global store.
 */

import { useState, useEffect, useRef } from 'react';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { colors } from '@/theme';
import { useAlishaChatStore } from '@/stores/alishaChat';

interface AlishaFloaterProps {
  /** Placeholder text for the input field */
  placeholder?: string;
}

export function AlishaFloater({
  placeholder = "Ask Irys anything..."
}: AlishaFloaterProps) {
  const {
    messages,
    isExpanded,
    isLoading,
    sendMessage,
    setExpanded,
    toggleExpanded,
    subscribeToWebSocket,
  } = useAlishaChatStore();
  const [input, setInput] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Subscribe to WebSocket for real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToWebSocket();
    return () => {
      unsubscribe();
    };
  }, [subscribeToWebSocket]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const messageText = input;
    setInput('');

    // Send message to Irys API (async)
    await sendMessage(messageText);

    // Scroll to bottom after message is added
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Scroll to bottom when chat opens or new messages arrive
  useEffect(() => {
    if (isExpanded) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  }, [isExpanded, messages.length]);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    }

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, setExpanded]);

  return (
    <div ref={containerRef} className="fixed bottom-6 right-6 z-50">
      {isExpanded && (
        <div
          className="mb-4 w-96 rounded-xl overflow-hidden shadow-2xl"
          style={{ background: colors.ironCharcoal, border: `1px solid ${colors.forgeSteel}` }}
        >
          {/* Header */}
          <div
            className="p-4 flex items-center justify-between"
            style={{
              background: colors.ironCharcoal,
              borderBottom: `1px solid ${colors.forgeSteel}`,
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${colors.foundryCopper}, ${colors.moltenGold})` }}
              >
                <Sparkles size={22} style={{ color: colors.obsidian }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: colors.sandstone }}>
                  Irys
                </h3>
                <p className="text-xs flex items-center gap-1.5" style={{ color: colors.sandstone }}>
                  AI Chief of Staff
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: colors.neonMint }} />
                    <span style={{ color: colors.neonMint }}>Online</span>
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="p-2 rounded-lg transition-all hover:scale-110"
              style={{ color: colors.secondaryText }}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          {/* Messages */}
          <div className="p-4 h-64 overflow-auto">
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${message.sender === 'user' ? 'justify-end' : ''}`}
                >
                  {message.sender === 'iris' && (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `linear-gradient(135deg, ${colors.foundryCopper}, ${colors.moltenGold})` }}
                    >
                      <Sparkles size={16} style={{ color: colors.obsidian }} />
                    </div>
                  )}
                  <div
                    className={`p-3 rounded-lg max-w-[80%] ${message.sender === 'iris' ? 'rounded-tl-none' : 'rounded-tr-none'}`}
                    style={{
                      background: message.sender === 'user' ? colors.forgeSteel : colors.obsidian,
                    }}
                  >
                    <p className="text-sm" style={{ color: colors.sandstone }}>
                      {message.text}
                    </p>
                    <span
                      className="text-xs mt-2 block"
                      style={{ color: message.sender === 'user' ? colors.sandstone : colors.forgeSteel, opacity: message.sender === 'user' ? 0.6 : 1 }}
                    >
                      {message.time}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Input */}
          <div className="p-4" style={{ borderTop: `1px solid ${colors.forgeSteel}` }}>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 px-4 py-2 rounded-lg outline-none transition-all"
                style={{
                  background: colors.obsidian,
                  border: `1px solid ${colors.forgeSteel}`,
                  color: colors.sandstone,
                }}
                onFocus={(e) => e.target.style.boxShadow = `0 0 0 2px ${colors.electricTeal}`}
                onBlur={(e) => e.target.style.boxShadow = 'none'}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading}
                className="px-4 py-2 rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: colors.moltenGold, color: colors.obsidian }}
              >
                {isLoading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Send size={20} strokeWidth={2.5} />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={toggleExpanded}
        className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
        style={{
          background: `linear-gradient(135deg, ${colors.foundryCopper}, ${colors.emberOrange})`,
          boxShadow: `0 4px 20px ${colors.foundryCopper}40`,
        }}
        aria-label="Chat with Irys"
      >
        <Sparkles size={32} style={{ color: colors.sandstone }} />
      </button>
    </div>
  );
}
